'use client';

import Icon from '@/components/ui/AppIcon';
import { XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';

interface PerformanceMetricsProps {
  timeRange: string;
  detailed?: boolean;
}

const mockPerformanceData = {
  totalShipments: 1247,
  activeShipments: 342,
  completedShipments: 905,
  averageTransitTime: 4.2,
  onTimeDelivery: 94.5,
  delayedShipments: 68,
  trendData: [
    { date: 'Week 1', shipments: 280, completed: 265, delayed: 15 },
    { date: 'Week 2', shipments: 310, completed: 295, delayed: 15 },
    { date: 'Week 3', shipments: 325, completed: 305, delayed: 20 },
    { date: 'Week 4', shipments: 332, completed: 315, delayed: 17 },
  ],
  routePerformance: [
    { route: 'USA → UK', shipments: 245, avgTime: 3.5, onTime: 96 },
    { route: 'USA → Asia', shipments: 198, avgTime: 5.2, onTime: 92 },
    { route: 'USA → Europe', shipments: 167, avgTime: 4.1, onTime: 95 },
    { route: 'USA → Australia', shipments: 134, avgTime: 6.8, onTime: 89 },
    { route: 'Domestic', shipments: 503, avgTime: 2.3, onTime: 98 },
  ],
};

export default function PerformanceMetrics({ timeRange, detailed = false }: PerformanceMetricsProps) {
  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center justify-between mb-2">
            <Icon name="public/assets/images/ChatGPT_Image_Feb_7_2026_02_48_47_AM-1770428942096.png" size={24} className="text-primary" />
            <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">+12%</span>
          </div>
          <h3 className="text-sm font-medium text-muted-foreground mb-1">Total Shipments</h3>
          <p className="text-3xl font-bold text-foreground">{mockPerformanceData.totalShipments.toLocaleString()}</p>
        </div>

        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center justify-between mb-2">
            <Icon name="ClockIcon" size={24} className="text-secondary" />
            <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">-0.3d</span>
          </div>
          <h3 className="text-sm font-medium text-muted-foreground mb-1">Avg Transit Time</h3>
          <p className="text-3xl font-bold text-foreground">{mockPerformanceData.averageTransitTime} days</p>
        </div>

        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center justify-between mb-2">
            <Icon name="CheckCircleIcon" size={24} className="text-green-600" />
            <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">+2.1%</span>
          </div>
          <h3 className="text-sm font-medium text-muted-foreground mb-1">On-Time Delivery</h3>
          <p className="text-3xl font-bold text-foreground">{mockPerformanceData.onTimeDelivery}%</p>
        </div>

        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center justify-between mb-2">
            <Icon name="ExclamationTriangleIcon" size={24} className="text-orange-500" />
            <span className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-1 rounded">-5</span>
          </div>
          <h3 className="text-sm font-medium text-muted-foreground mb-1">Delayed Shipments</h3>
          <p className="text-3xl font-bold text-foreground">{mockPerformanceData.delayedShipments}</p>
        </div>
      </div>

      {/* Shipment Trends Chart */}
      <div className="bg-card rounded-xl p-6 shadow-card border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4">Shipment Trends</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={mockPerformanceData.trendData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="date" stroke="#6b7280" />
            <YAxis stroke="#6b7280" />
            <Tooltip
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
            />
            <Legend />
            <Line type="monotone" dataKey="shipments" stroke="#3b82f6" strokeWidth={2} name="Total Shipments" />
            <Line type="monotone" dataKey="completed" stroke="#10b981" strokeWidth={2} name="Completed" />
            <Line type="monotone" dataKey="delayed" stroke="#f59e0b" strokeWidth={2} name="Delayed" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Route Performance */}
      {detailed && (
        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <h3 className="text-lg font-semibold text-foreground mb-4">Route Performance</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Route</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Shipments</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Avg Time</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">On-Time %</th>
                </tr>
              </thead>
              <tbody>
                {mockPerformanceData.routePerformance.map((route, index) => (
                  <tr key={index} className="border-b border-border hover:bg-muted/50 transition-colors">
                    <td className="py-3 px-4 text-sm font-medium text-foreground">{route.route}</td>
                    <td className="py-3 px-4 text-sm text-right text-foreground">{route.shipments}</td>
                    <td className="py-3 px-4 text-sm text-right text-foreground">{route.avgTime} days</td>
                    <td className="py-3 px-4 text-sm text-right">
                      <span
                        className={`font-medium ${
                          route.onTime >= 95 ? 'text-green-600' : route.onTime >= 90 ? 'text-orange-600' : 'text-red-600'
                        }`}
                      >
                        {route.onTime}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}